/**
 * Touch and mouse input for <magnify-dialog> (assets/magnify-dialog.js).
 *
 * Touch is read from Touch Events, not Pointer Events: a two-finger
 * touchstart is cancelled (non-passive) before the browser can turn it into a
 * scroll or a page zoom, which would fire pointercancel and kill the pinch
 * halfway. Double tap is timed here, because `dblclick` never fires reliably
 * on iOS Safari. Mouse keeps Pointer Events, with Dawn's close-on-pointerup
 * limited to the backdrop.
 *
 * The dialog owns slides and zoom state; this class only turns gestures into
 * calls on it.
 */
window.MagnifyGestures = class MagnifyGestures {
  static TAP_SLOP_PX = 10;
  static DOUBLE_TAP_MS = 300;
  static DOUBLE_TAP_PX = 30;
  static EDGE_RELEASE_PX = 64;
  static WHEEL_FACTOR = 1.15;

  static point(source) {
    return { x: source.clientX, y: source.clientY };
  }

  constructor(dialog) {
    this.dialog = dialog;
    this.geometry = dialog.geometry;
    this.clear();
  }

  clear() {
    this.gesture = null;
    this.tap = null;
    this.lastTap = null;
    this.mouse = null;
  }

  bind() {
    const { modal, stage } = this.dialog;
    const active = { passive: false };
    // On the whole modal, so a pinch over the close button or the controls
    // never zooms the page behind the dialog either.
    modal.addEventListener('touchstart', this.onTouchStart.bind(this), active);
    modal.addEventListener('touchmove', this.onTouchMove.bind(this), active);
    modal.addEventListener('touchend', this.onTouchEnd.bind(this), active);
    modal.addEventListener('touchcancel', this.onTouchCancel.bind(this));
    // iOS Safari pinch-zooms the page through its own gesture* events.
    ['gesturestart', 'gesturechange'].forEach((type) =>
      modal.addEventListener(type, (event) => event.preventDefault(), active)
    );

    stage.addEventListener('wheel', this.onWheel.bind(this), active);
    stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
    stage.addEventListener('pointermove', this.onPointerMove.bind(this));
    // Capture phase: runs before Dawn's pointerup on the modal, which closes it.
    stage.addEventListener('pointerup', this.onPointerUp.bind(this), true);
    stage.addEventListener('pointercancel', () => this.endMouse(), true);
  }

  /* ---------- Touch ---------- */

  onTouchStart(event) {
    const { dialog } = this;
    if (event.touches.length < 2 && !dialog.stage.contains(event.target)) return;
    const image = dialog.imageFromTarget(event.target) || dialog.currentImage();

    if (event.touches.length >= 2) {
      event.preventDefault();
      this.tap = null;
      if (image) this.startPinch(event.touches, image);
      return;
    }

    const point = MagnifyGestures.point(event.touches[0]);
    this.tap = { ...point, time: event.timeStamp, valid: Boolean(image || this.dialog.state.scale > 1), image };
    if (image && dialog.state.scale > 1 && dialog.state.image === image) this.startPan(point);
  }

  onTouchMove(event) {
    // Zoomed or mid-pinch, no finger may scroll the track: iOS does not apply a
    // touch-action change to a gesture already running, so cancel it here too.
    if (this.dialog.isLocked() && event.cancelable && this.dialog.stage.contains(event.target)) event.preventDefault();
    if (this.tap?.valid) {
      const moved = this.geometry.distance(this.tap, MagnifyGestures.point(event.touches[0]));
      if (moved > MagnifyGestures.TAP_SLOP_PX) this.tap.valid = false;
    }
    if (!this.gesture) return;

    if (this.gesture.type === 'pinch' && event.touches.length >= 2) {
      event.preventDefault();
      this.updatePinch(event.touches);
    } else if (this.gesture.type === 'pan' && event.touches.length === 1) {
      event.preventDefault();
      this.updatePan(MagnifyGestures.point(event.touches[0]));
    }
  }

  onTouchEnd(event) {
    if (this.gesture?.type === 'pinch') {
      if (event.touches.length === 1 && this.dialog.state.scale > 1) {
        this.startPan(MagnifyGestures.point(event.touches[0]));
      } else if (event.touches.length === 0) {
        this.endGesture();
      }
      return;
    }
    if (this.gesture?.type === 'pan') this.finishPan();

    const tap = this.tap;
    this.tap = null;
    if (!tap?.valid || event.touches.length > 0) return;
    if (event.timeStamp - tap.time > MagnifyGestures.DOUBLE_TAP_MS) return;

    const current = { x: tap.x, y: tap.y, time: event.timeStamp };
    const { DOUBLE_TAP_MS, DOUBLE_TAP_PX } = MagnifyGestures;
    if (!this.geometry.isDoubleTap(this.lastTap, current, DOUBLE_TAP_MS, DOUBLE_TAP_PX)) {
      this.lastTap = current;
      return;
    }
    // Stops the synthetic click/dblclick and any native double-tap zoom.
    event.preventDefault();
    this.lastTap = null;
    // Zoomed, a double tap always returns the zoomed image to 100%, wherever
    // on the slide it lands.
    const zoomedImage = this.dialog.state.scale > 1 ? this.dialog.state.image : null;
    this.dialog.toggleZoom(zoomedImage || tap.image, tap);
  }

  onTouchCancel() {
    this.tap = null;
    if (this.gesture) this.endGesture();
  }

  startPinch(touches, image) {
    const { dialog } = this;
    dialog.snapToSlide();
    dialog.setActiveImage(image);
    const first = MagnifyGestures.point(touches[0]);
    const second = MagnifyGestures.point(touches[1]);
    this.gesture = {
      type: 'pinch',
      distance: this.geometry.distance(first, second) || 1,
      center: this.geometry.midpoint(first, second),
      scale: dialog.state.scale,
      x: dialog.state.x,
      y: dialog.state.y,
    };
    dialog.setGesturing(true);
  }

  updatePinch(touches) {
    const { dialog, geometry, gesture: start } = this;
    const first = MagnifyGestures.point(touches[0]);
    const second = MagnifyGestures.point(touches[1]);
    const center = geometry.midpoint(first, second);
    const scale = dialog.clampScale((start.scale * geometry.distance(first, second)) / start.distance);
    const box = dialog.box();
    const origin = { x: start.center.x - box.viewLeft, y: start.center.y - box.viewTop };
    // Zoom around where the fingers landed, then follow them as they move.
    dialog.state.x = geometry.offsetAround(start.x, start.scale, scale, origin.x, box.left) + center.x - start.center.x;
    dialog.state.y = geometry.offsetAround(start.y, start.scale, scale, origin.y, box.top) + center.y - start.center.y;
    dialog.state.scale = scale;
    dialog.applyTransform();
  }

  startPan(point) {
    const { state } = this.dialog;
    this.gesture = { type: 'pan', start: point, x: state.x, y: state.y, overshootX: 0 };
    this.dialog.setGesturing(true);
  }

  updatePan(point) {
    const { dialog, gesture: start } = this;
    const rawX = start.x + point.x - start.start.x;
    dialog.state.x = rawX;
    dialog.state.y = start.y + point.y - start.start.y;
    dialog.applyTransform();
    start.overshootX = rawX - dialog.state.x;
  }

  /** A drag that keeps going past the image edge moves on to the next image. */
  finishPan() {
    const overshoot = this.gesture.overshootX;
    this.endGesture();
    if (!this.dialog.isCarousel() || Math.abs(overshoot) < MagnifyGestures.EDGE_RELEASE_PX) return;
    this.dialog.goToSlide(this.dialog.currentIndex() + (overshoot < 0 ? 1 : -1));
  }

  endGesture() {
    this.gesture = null;
    this.dialog.setGesturing(false);
    this.dialog.settle();
  }

  /* ---------- Mouse ---------- */

  onPointerDown(event) {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    const { dialog } = this;
    const image = dialog.imageFromTarget(event.target);
    if (!image) return;
    this.mouse = { id: event.pointerId, image, start: MagnifyGestures.point(event), moved: false };
    if (dialog.state.scale <= 1 || dialog.state.image !== image) return;
    this.startPan(this.mouse.start);
    image.setPointerCapture?.(event.pointerId);
    image.classList.add('is-panning');
  }

  onPointerMove(event) {
    if (!this.mouse || event.pointerId !== this.mouse.id) return;
    const point = MagnifyGestures.point(event);
    if (this.geometry.distance(this.mouse.start, point) > MagnifyGestures.TAP_SLOP_PX / 2) this.mouse.moved = true;
    if (this.gesture?.type !== 'pan') return;
    event.preventDefault();
    this.updatePan(point);
  }

  onPointerUp(event) {
    if (!this.mouse || event.pointerId !== this.mouse.id) return;
    // A click on an image zooms; only a click on the backdrop closes the modal.
    event.stopPropagation();
    const { image, moved } = this.mouse;
    this.endMouse();
    if (!moved) this.dialog.toggleZoom(image, MagnifyGestures.point(event));
  }

  endMouse() {
    this.mouse?.image.classList.remove('is-panning');
    this.mouse = null;
    if (this.gesture?.type === 'pan') this.endGesture();
  }

  onWheel(event) {
    const { dialog } = this;
    const image = dialog.imageFromTarget(event.target);
    // A plain wheel scrolls the desktop list; ctrl+wheel is a trackpad pinch.
    if (!image || (!event.ctrlKey && dialog.state.scale <= 1)) return;
    event.preventDefault();
    dialog.setActiveImage(image);
    const factor = event.deltaY < 0 ? MagnifyGestures.WHEEL_FACTOR : 1 / MagnifyGestures.WHEEL_FACTOR;
    dialog.zoomTo(dialog.state.scale * factor, MagnifyGestures.point(event));
  }
};
